import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Guest cart helper functions
const getGuestCart = () => JSON.parse(localStorage.getItem('guestCart') || '[]');
const saveGuestCart = (cart) => localStorage.setItem('guestCart', JSON.stringify(cart));

export default function BodyWashPage() {
  const { user, fetchCartCount } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        // Filter for body wash products
        const washProducts = response.data.filter(p => 
          p.type === 'whipped_soap' || p.type === 'bar_soap'
        );
        setProducts(washProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const addToCart = (productId) => {
    const product = products.find(p => p.id === productId);
    
    if (user) {
      axios.post(`${API}/cart`, { item_id: productId, item_type: 'product', quantity: 1 }, { withCredentials: true })
        .then(() => {
          fetchCartCount();
          toast.success('Added to cart!');
        })
        .catch(() => {
          toast.error('Failed to add to cart');
        });
    } else {
      // Guest cart
      const guestCart = getGuestCart();
      const existingItem = guestCart.find(item => item.cart_item.item_id === productId);
      
      if (existingItem) {
        existingItem.cart_item.quantity += 1;
      } else {
        const timestamp = new Date().getTime();
        guestCart.push({
          cart_item: {
            id: `guest_${timestamp}`,
            item_id: productId,
            item_type: 'product',
            quantity: 1
          },
          item_details: {
            id: product.id,
            name: product.name,
            price: product.price,
            image_url: product.image_url,
            type: product.type
          }
        });
      }
      
      saveGuestCart(guestCart);
      toast.success('Added to cart!');
      window.dispatchEvent(new Event('guestCartUpdated'));
    }
  };

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        backgroundImage: 'linear-gradient(rgba(129, 216, 208, 0.92), rgba(129, 216, 208, 0.92)), url(https://images.unsplash.com/photo-1556228841-4f71338a4a00?q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Sparkles className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            JFRAI Family Creations
          </h1>
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Whipped Body Wash & Bar Soaps
          </h2>
          <p className="text-xl text-white max-w-3xl mx-auto" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
            Luxurious, creamy whipped body washes and artisan bar soaps that cleanse while nourishing. 
            Made with natural ingredients and essential oils for a spa-like experience every day.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map(product => (
            <Card key={product.id} className="overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img 
                  src={product.image_url} 
                  alt={product.name} 
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                />
              </div>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {product.name}
                  </CardTitle>
                  {product.scent && (
                    <Badge className="bg-[#00A86B]">
                      {product.scent}
                    </Badge>
                  )}
                </div>
                <CardDescription className="text-base mt-2">
                  {product.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-[#00A86B]">
                    ${product.price.toFixed(2)}
                  </span>
                  <Button 
                    onClick={() => addToCart(product.id)}
                    className="bg-[#00A86B] hover:bg-[#008f5d]"
                  >
                    Add to Cart
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Benefits Section */}
        <Card className="mt-16 bg-white/95 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-3xl text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Our Body Wash & Soaps?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <h3 className="text-xl font-semibold text-sage-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Gentle & Nourishing
                </h3>
                <p className="text-sage-700">
                  Our whipped body washes and soaps cleanse without stripping natural oils. 
                  Perfect for all skin types, even sensitive skin.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-sage-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Rich Lather
                </h3>
                <p className="text-sage-700">
                  Experience a luxurious, creamy lather that transforms your daily shower 
                  into a spa-like indulgence.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-sage-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Natural Scents
                </h3>
                <p className="text-sage-700">
                  Infused with pure essential oils and botanical extracts for authentic, 
                  long-lasting fragrances that uplift your senses.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
